import { ViteSSG } from 'vite-ssg'
import { createUnhead as createHead } from '@unhead/vue'
import { createPinia } from 'pinia'
import { plugin as formkitPlugin, defaultConfig as formkitConfig } from '@formkit/vue'
import * as Sentry from '@sentry/vue'
import App from './App.vue'
import { routes } from './router'
import i18n from './i18n'
import { getTranslation } from './api/mts'
import { getLocation } from './api/location'
import { localeFromPath, localePrefix } from './utils'
import { SENTRY_PROID, SENTRY_PROKEY, SENTRY_TAG, SENTRY_ENV } from './config'
import './less/main.less'
import 'vue3-carousel/dist/carousel.css'

const LOCALE_BY_COUNTRY: Record<string, string> = {
  CN: 'sc',
  TW: 'tc',
  HK: 'tc',
  MO: 'tc',
}

export const createApp = ViteSSG(
  App,
  {
    routes,
    base: import.meta.env.BASE_URL,
    scrollBehavior() {
      return { top: 0, left: 0 }
    },
  },
  async ({ app, router, isClient }) => {
    const head = createHead()
    app.use(head)
    app.use(i18n)
    app.use(createPinia())
    app.use(formkitPlugin, formkitConfig)

    // Locale path prefix for templates: `${$localePrefix()}/contactus`
    app.config.globalProperties.$localePrefix = () =>
      localePrefix(i18n.global.locale.value)

    // Locale loading guard — works during both SSG and client navigation
    router.beforeEach(async (to, _from, next) => {
      const locale = localeFromPath(to.path)

      if (!i18n.global.availableLocales.includes(locale)) {
        // During SSG, getTranslation will return fallback immediately
        const messages = await getTranslation(locale)
        if (Object.keys(messages).length > 0) {
          i18n.global.setLocaleMessage(locale, messages)
        }
      }

      i18n.global.locale.value = locale

      if (isClient) {
        document.documentElement.setAttribute('lang', locale)
      }

      return next()
    })

    // Sentry — client only (requires browser APIs)
    if (isClient) {
      // On first hit to the EN home, show it immediately; then swap to the
      // IP-matched locale in the background if it differs.
      let firstNav = true
      router.afterEach(async (to) => {
        if (!firstNav) return
        firstNav = false
        if (to.path !== '/') return
        try {
          const result = await getLocation()
          const code = (result as any)?.data?.country_code?.toUpperCase()
          const target = LOCALE_BY_COUNTRY[code]
          if (target) router.replace(`/${target}/`)
        } catch {
          // keep EN
        }
      })

      const Vue3Lottie = (await import('vue3-lottie')).default
      app.use(Vue3Lottie)

      const domain = window.location.hostname
      Sentry.init({
        app,
        dsn: `https://${SENTRY_PROKEY}@${domain}/sentry/${SENTRY_PROID}`,
        release: SENTRY_TAG,
        environment: SENTRY_ENV,
        integrations: [
          Sentry.browserTracingIntegration({ router }),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        debug: false,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      })
    }
  },
)
